import {
	IconCheckOutline18 as CheckIcon,
	IconChevronDownOutline18 as ChevronDownIcon,
} from "nucleo-ui-outline-18"

import type { IconType } from "@productlane/ui"
import { DropdownMenu, DropdownMenuItem } from "@productlane/ui"
import {
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"

export interface SettingsDropdownItem {
	value: string
	label: string
	icon: IconType
}

/** An option with `children` opens them in a side menu on hover. */
export interface SettingsDropdownOption extends SettingsDropdownItem {
	children?: SettingsDropdownItem[]
}

/** The selected option, top-level rows first so a parent wins over a child sharing its value. */
export function findDropdownOption(
	value: string | null | undefined,
	options: readonly SettingsDropdownOption[],
): SettingsDropdownItem | undefined {
	const items = [...options, ...options.flatMap((o) => o.children ?? [])]
	return items.find((item) => item.value === value)
}

/** A settings select whose options can open a side menu, which Radix Select cannot. */
export function SettingsDropdown({
	value,
	options,
	disabled,
	onChange,
}: {
	value: string
	options: readonly SettingsDropdownOption[]
	disabled?: boolean
	onChange: (value: string) => void
}) {
	const selected = findDropdownOption(value, options) ?? options[0]

	const item = (option: SettingsDropdownItem) => (
		<DropdownMenuItem
			key={option.value}
			Icon={option.icon}
			IconRight={option.value === selected?.value ? CheckIcon : undefined}
			onSelect={() => onChange(option.value)}
		>
			<span className="truncate">{option.label}</span>
		</DropdownMenuItem>
	)

	return (
		<DropdownMenu
			contentProps={{ align: "end" }}
			trigger={
				// Matches the Select trigger, so it sits next to other settings selects unchanged.
				<button
					type="button"
					disabled={disabled}
					className="flex h-8 max-w-60 items-center gap-2 rounded-md bg-white pl-3 pr-2 text-sm shadow-sm ring-1 ring-inset ring-black/15 focus:outline-none disabled:cursor-not-allowed dark:bg-gray-700/70 dark:text-gray-50 dark:ring-white/15"
				>
					{selected ? (
						<selected.icon className="size-4 flex-none text-tertiary" />
					) : null}
					<span className="flex-1 truncate text-left">{selected?.label}</span>
					<ChevronDownIcon
						className="h-3 w-3 flex-none opacity-50"
						strokeWidth={1.75}
					/>
				</button>
			}
		>
			{options.map((option) =>
				option.children ? (
					<DropdownMenuSub key={option.value}>
						<DropdownMenuSubTrigger Icon={option.icon}>
							{option.label}
						</DropdownMenuSubTrigger>
						<DropdownMenuPortal>
							<DropdownMenuSubContent className="max-h-80 w-56 overflow-y-auto pb-2">
								{option.children.map(item)}
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>
				) : (
					item(option)
				),
			)}
		</DropdownMenu>
	)
}
